/**
 * @fileoverview Filter chips component for displaying active filters as removable chips
 * @module ctl-filter-chips
 * @version 1.0.0
 * 
 * @description
 * Displays active filters as removable chips/pills. Each chip can be removed individually,
 * and all chips can be cleared at once.
 * 
 * @example
 * <!-- Basic usage -->
 * <ctl-filter-chips></ctl-filter-chips>
 * 
 * @example
 * <!-- With initial filters -->
 * <ctl-filter-chips 
 *   filters='[{"label": "5x10", "value": "5x10"}, {"label": "Climate controlled", "value": "climate"}]'
 * ></ctl-filter-chips>
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-font: system-ui, -apple-system, Segoe UI, Roboto, Arial;
    display: block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .container {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-3);
    flex-wrap: wrap;
  }

  .chips {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2);
    flex-wrap: wrap;
    flex: 1;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-1-5);
    padding: var(--ctl-space-1-5) var(--ctl-space-3);
    background: color-mix(in srgb, var(--ctl-color-brand) 10%, var(--ctl-color-surface));
    color: var(--ctl-color-brand);
    border: 1px solid color-mix(in srgb, var(--ctl-color-brand) 20%, var(--ctl-color-border));
    border-radius: var(--ctl-radius-full);
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-normal);
    transition: background .15s ease, border-color .15s ease;
  }

  .chip:hover {
    background: color-mix(in srgb, var(--ctl-color-brand) 15%, var(--ctl-color-surface));
    border-color: color-mix(in srgb, var(--ctl-color-brand) 30%, var(--ctl-color-border));
  }

  .chip-label {
    user-select: none;
  }

  .chip-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--ctl-space-4-5);
    height: var(--ctl-space-4-5);
    border: none;
    background: transparent;
    color: currentColor;
    cursor: pointer;
    border-radius: 50%;
    padding: 0;
    font-size: var(--ctl-font-size-base);
    line-height: 1;
    transition: background .15s ease;
    margin-right: calc(var(--ctl-space-1) * -1);
  }

  .chip-remove:hover {
    background: color-mix(in srgb, var(--ctl-color-brand) 20%, transparent);
  }

  .clear-all {
    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-1);
    padding: var(--ctl-space-1-5) var(--ctl-space-3);
    border: 1px solid var(--ctl-color-border);
    background: transparent;
    color: var(--ctl-color-muted);
    border-radius: var(--ctl-radius-full);
    font-family: inherit;
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-normal);
    cursor: pointer;
    transition: background .15s ease, border-color .15s ease, color .15s ease;
    white-space: nowrap;
  }

  .clear-all:hover {
    background: var(--ctl-color-overlay-1);
    border-color: var(--ctl-color-text);
    color: var(--ctl-color-text);
  }

  .empty {
    color: var(--ctl-color-muted);
    font-size: var(--ctl-font-size-base);
    padding: var(--ctl-space-2) 0;
  }

  :host([hidden]) {
    display: none;
  }
</style>

<div class="container" part="container">
  <div class="chips" part="chips"></div>
  <button class="clear-all" part="clear-all" hidden>Clear all</button>
</div>
<div class="empty" part="empty" hidden>No active filters</div>
`;

/**
 * Filter Chips Web Component
 * 
 * @class CtlFilterChips
 * @extends HTMLElement
 * 
 * @attr {string} filters - JSON array of filter objects with label and value
 * @attr {boolean} show-empty - Show "No active filters" message when empty
 * 
 * @fires ctl-remove - Dispatched when a filter chip is removed
 * @fires ctl-remove.detail.filter - The removed filter object
 * @fires ctl-clear-all - Dispatched when "Clear all" is clicked
 * @fires ctl-change - Dispatched when filters change
 * @fires ctl-change.detail.filters - Current filters array
 * 
 * @csspart container - The main container
 * @csspart chips - The chips container
 * @csspart clear-all - The clear all button
 * @csspart empty - The empty state message
 * 
 * @cssprop --ctl-color-text - Text color
 * @cssprop --ctl-color-muted - Muted text color
 * @cssprop --ctl-color-border - Border color
 * @cssprop --ctl-color-surface - Background color
 * @cssprop --ctl-color-brand - Brand color for chips
 */
export class CtlFilterChips extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(template.content.cloneNode(true));
    
    this._chips = this.shadowRoot.querySelector('.chips');
    this._clearAll = this.shadowRoot.querySelector('.clear-all');
    this._empty = this.shadowRoot.querySelector('.empty');
    this._filters = [];
    
    // Bind handler once
    this._boundHandleClearAll = this._handleClearAll.bind(this);
  }

  static get observedAttributes() {
    return ['filters', 'show-empty'];
  }

  connectedCallback() {
    this._clearAll.addEventListener('click', this._boundHandleClearAll);
    this._render();
  }

  disconnectedCallback() {
    this._clearAll.removeEventListener('click', this._boundHandleClearAll);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      if (name === 'filters') {
        this._parseFilters();
      }
      this._render();
    }
  }

  _parseFilters() {
    try {
      const parsed = JSON.parse(this.getAttribute('filters') || '[]');
      this._filters = Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      this._filters = [];
    }
  }

  _handleRemove(filter, index) {
    this._filters.splice(index, 1);
    this.setAttribute('filters', JSON.stringify(this._filters));
    
    this.dispatchEvent(new CustomEvent('ctl-remove', {
      bubbles: true,
      composed: true,
      detail: { filter, filters: this._filters }
    }));

    this.dispatchEvent(new CustomEvent('ctl-change', {
      bubbles: true,
      composed: true,
      detail: { filters: this._filters }
    }));
  }

  _handleClearAll() {
    this._filters = [];
    this.setAttribute('filters', '[]');
    
    this.dispatchEvent(new CustomEvent('ctl-clear-all', {
      bubbles: true,
      composed: true
    }));

    this.dispatchEvent(new CustomEvent('ctl-change', {
      bubbles: true,
      composed: true,
      detail: { filters: [] }
    }));
  }

  _render() {
    const hasFilters = this._filters.length > 0;
    
    // Show/hide clear all button
    this._clearAll.hidden = !hasFilters;

    // Show/hide empty message
    const showEmpty = this.hasAttribute('show-empty');
    this._empty.hidden = hasFilters || !showEmpty;

    // Render chips
    this._chips.innerHTML = '';
    
    this._filters.forEach((filter, index) => {
      const chip = document.createElement('div');
      chip.className = 'chip';
      chip.part = 'chip';
      
      const label = document.createElement('span');
      label.className = 'chip-label';
      label.textContent = filter.label || filter.value || '';
      
      const removeBtn = document.createElement('button');
      removeBtn.className = 'chip-remove';
      removeBtn.setAttribute('aria-label', `Remove ${filter.label || filter.value}`);
      removeBtn.innerHTML = '×';
      removeBtn.addEventListener('click', () => this._handleRemove(filter, index));
      
      chip.appendChild(label);
      chip.appendChild(removeBtn);
      this._chips.appendChild(chip);
    });
  }

  /**
   * Get the current filters
   * @returns {Array} The current filters array
   */
  get filters() {
    return [...this._filters];
  }

  /**
   * Set the filters
   * @param {Array} filters - Array of filter objects
   */
  set filters(filters) {
    this._filters = Array.isArray(filters) ? filters : [];
    this.setAttribute('filters', JSON.stringify(this._filters));
  }

  /**
   * Add a filter
   * @param {Object} filter - Filter object with label and value
   */
  addFilter(filter) {
    if (!filter || typeof filter !== 'object') return;
    this._filters.push(filter);
    this.setAttribute('filters', JSON.stringify(this._filters));
    
    this.dispatchEvent(new CustomEvent('ctl-change', {
      bubbles: true,
      composed: true,
      detail: { filters: this._filters }
    }));
  }

  /**
   * Remove a filter by value
   * @param {string} value - Value of the filter to remove
   */
  removeFilter(value) {
    const index = this._filters.findIndex(f => f.value === value);
    if (index !== -1) {
      this._handleRemove(this._filters[index], index);
    }
  }

  /**
   * Clear all filters
   */
  clearAll() {
    this._handleClearAll();
  }
}

if (!customElements.get("ctl-filter-chips")) {
  customElements.define("ctl-filter-chips", CtlFilterChips);
}
